import ConvertUtil from "utils/ConvertUtil.js";

/**
 * Calculate Velocity Made Good (VMG) for a point
 * VMG measures the effective speed toward or away from the wind direction
 *
 * @param {number} speedInKMH - Boat speed in km/h
 * @param {number} bearing - Boat course/bearing in degrees (0-360)
 * @param {number} windAngle - Wind direction in degrees (0-360)
 * @return {number} VMG in km/h
 */
export const calculateVMG = (speedInKMH: number, bearing: number, windAngle: number): number => {
  if (!speedInKMH || speedInKMH <= 0 || bearing < 0 || windAngle < 0) {
    return 0;
  }

  // Normalize angles to 0-360
  const normalizedBearing = ((bearing % 360) + 360) % 360;
  const normalizedWindAngle = ((windAngle % 360) + 360) % 360;

  // Calculate the angle between course and wind direction (True Wind Angle - TWA)
  let angleDiff = Math.abs(normalizedBearing - normalizedWindAngle);

  // Handle the case where the angle wraps around 360/0
  // TWA should be between 0 and 180 degrees
  if (angleDiff > 180) {
    angleDiff = 360 - angleDiff;
  }

  // TWA is the angle between course and wind direction (0-180°)
  const twa = angleDiff;

  // Convert to radians for cosine calculation
  const twaRad = ConvertUtil.convertDegreeToRadian(twa);

  // Calculate VMG
  // VMG = Speed × cos(TWA)
  // This formula works for both upwind and downwind
  // For upwind (TWA < 90°): positive VMG
  // For downwind (TWA > 90°): still positive VMG (we're moving away from wind)
  const vmg = speedInKMH * Math.cos(twaRad);

  // Return absolute value (VMG is always positive)
  return Math.abs(vmg);
};

/**
 * Calculate average VMG from an array of points
 *
 * @param {Array} points - Array of points with speedInKMH, bearing, and activity windAngle
 * @param {number} windAngle - Wind direction in degrees
 * @return {number} Average VMG in km/h
 */
export const calculateAverageVMG = (points: any[], windAngle: number): number => {
  if (!points || points.length === 0 || windAngle < 0) {
    return 0;
  }

  let totalVMG = 0;
  let validPoints = 0;

  points.forEach((point) => {
    if (point.speedInKMH > 0 && point.bearing >= 0) {
      const vmg = calculateVMG(point.speedInKMH, point.bearing, windAngle);
      totalVMG += vmg;
      validPoints++;
    }
  });

  return validPoints > 0 ? totalVMG / validPoints : 0;
};

/**
 * Calculate VMG for each point in an array
 *
 * @param {Array} points - Array of points
 * @param {number} windAngle - Wind direction in degrees
 * @return {Array} Array of points with vmgInKMH property added
 */
export const calculateVMGForPoints = (points: any[], windAngle: number): any[] => {
  if (!points || points.length === 0 || windAngle < 0) {
    return points;
  }

  return points.map((point) => {
    const vmg = calculateVMG(point.speedInKMH, point.bearing, windAngle);
    return {
      ...point,
      vmgInKMH: vmg
    };
  });
};

/**
 * Calculate VMG using inferred TWA directly
 * This is useful when TWA is inferred from track geometry rather than calculated from bearing/wind
 *
 * @param {number} speedInKMH - Boat speed in km/h
 * @param {number} inferredTWA - Inferred True Wind Angle in degrees (0-180)
 * @return {number} VMG in km/h
 */
export const calculateVMGFromInferredTWA = (speedInKMH: number, inferredTWA: number | null | undefined): number => {
  if (!speedInKMH || speedInKMH <= 0 || inferredTWA === null || inferredTWA === undefined) {
    return 0;
  }

  // TWA should be between 0 and 180 degrees
  const twa = Math.min(Math.max(inferredTWA, 0), 180);

  // Convert to radians for cosine calculation
  const twaRad = ConvertUtil.convertDegreeToRadian(twa);

  // Calculate VMG: Speed × cos(TWA)
  const vmg = speedInKMH * Math.cos(twaRad);

  // Return absolute value (VMG is always positive)
  return Math.abs(vmg);
};
